import React, { useState, useRef, useEffect } from 'react';
import { Info, Trash2, Menu } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import Tooltip from '../addentities/Tooltip.js';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import AlertNotification from '../../components/AlertNotification.js';
import LinkStockInstancesModal from './LinkStockInstancesModal.js'; // ← NEW
import apiHelper from '../../apiHelper.js';
import noImage from '../../assets/img/brand/noImage.png'

export default function PlatformManageOrder() {
    const history = useHistory();
    const location = useLocation();
    const isCancelled = location.state?.order?.status === 'Cancelled';
    console.log('Order status:', location.state?.order?.status);

    const getInitialFormData = () => {
        const order = location.state?.order;
        const orderId = order?.platformOrderId ?? order?.id;
        const storageKey = `platformManageOrder_formData_${orderId}`;
        const saved = orderId ? sessionStorage.getItem(storageKey) : null;
        if (saved) return JSON.parse(saved);
        const rawSettlement = order?.settlementPrice;
        let parsedSettlement = '';

        if (rawSettlement && rawSettlement !== '-') {
            // Remove currency (e.g. "SGD 9.00" → "9.00")
            parsedSettlement = rawSettlement.replace(/[^\d.]/g, '');
        }
        return {
            settlement_price: parsedSettlement,
            currency: 'SGD',
            linked_stock_instance: '',
            notes: '',
        };
    };

    const order = location.state?.order;
    const orderId = order?.platformOrderId ?? order?.id;
    const storageKey = `platformManageOrder_formData_${orderId}`;

    const [formData, setFormData] = useState(getInitialFormData);
    const [settlementFocused, setSettlementFocused] = useState(false);
    const [errors, setErrors] = useState({ settlement_price: '' });
    const [touchedFields, setTouchedFields] = useState({ settlement_price: false });
    const [disabled, setDisabled] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [alert, setAlert] = useState(null);
    const [showItemAlert, setShowItemAlert] = useState(false);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkedInstances, setLinkedInstances] = useState([]); // [{id, qty}]
    const [originalLinkedInstances, setOriginalLinkedInstances] = useState([]);
    const discardRef = useRef(null);
    const dragIndexRef = useRef(null);
    const platformLabel = location.state?.platformLabel || 'Platform';

    useEffect(() => {
        sessionStorage.setItem(storageKey, JSON.stringify(formData));
    }, [formData, storageKey]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (discardRef.current && !discardRef.current.contains(event.target)) {
                setDiscardModalOpen(false);
            }
        }
        if (discardModalOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [discardModalOpen]);

    const showAlert = (type, action, recordName) => {
        setAlert({ type, action, recordName });
        setShowItemAlert(true);
        setTimeout(() => setShowItemAlert(false), 5000);
    };

    const formatSettlementDisplay = (val) => {
        if (val === '' || val == null) return '';
        const num = parseFloat(String(val).replace(/,/g, ''));
        if (isNaN(num)) return val;
        return num.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const clean = name === 'settlement_price'
            ? value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
            : value;
        setFormData((prev) => ({ ...prev, [name]: clean }));
        if (!isDirty) setIsDirty(true);
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        return { settlement_price: '' };
    };

    const handleSave = async () => {
        setIsSaving(true);
        setDisabled(true);
        setTouchedFields({ settlement_price: true });

        const nextErrors = validateForm();
        setErrors(nextErrors);

        const hasErrors = Object.values(nextErrors).some(Boolean);
        if (hasErrors) {
            setDisabled(false);
            setIsSaving(false);
            showAlert('error', 'create', 'Manage Order');
            return;
        }

        try {
            console.log('Form data:', formData);
            console.log('Linked instances:', linkedInstances);

            const order = location.state?.order;
            console.log('Order from location state:', order);

            // ✅ FIX 1: correct order ID
            const orderId = order?.platformOrderId;

            if (!orderId) {
                console.error("Order ID missing");
                showAlert('error', 'create', 'Order ID missing');
                return;
            }

            const currentIds = linkedInstances.map(i => i.id);
            const originalIds = originalLinkedInstances.map(i => i.id);

            // ✅ 1. Linked (status = 1)
            const linkedPayload = linkedInstances.map((item, index) => ({
                platform_order_stockinstance_link_id: item.platform_order_stockinstance_link_id,
                platform_order_item_id: item.orderItemId || 1,
                stock_instances_id: item.stockInstanceId ?? item.id,
                linked_quantity: item.qty || 1,
                sort_order: index + 1,
                status: 1,
                branch_id: item.branchId,
                platform_name: platformLabel || 'N/A'
            }));

            // ✅ 2. Unlinked (status = 2)
            const unlinkedPayload = originalLinkedInstances
                .filter(item => !currentIds.includes(item.id))
                .map(item => ({
                    platform_order_stockinstance_link_id: item.platform_order_stockinstance_link_id,
                    platform_order_item_id: item.orderItemId || 1,
                    stock_instances_id: item.stockInstanceId ?? item.id,
                    linked_quantity: item.qty || 1,
                    status: 2,
                    branch_id: item.branchId,
                    platform_name: platformLabel || 'N/A'
                }));

            // ✅ Final payload
            const payload = {
                settlement_price: formData.settlement_price,
                linked_stockinstance_qty: linkedInstances.reduce(
                    (sum, item) => sum + (item.qty || 0),
                    0
                ),
                stock_links: [
                    ...linkedPayload,
                    ...unlinkedPayload
                ]
            };

            console.log('Final payload:', payload);

            // ✅ FIX 2: use PUT
            const response = await apiHelper(
                `sales/platform-order/${orderId}/link/`,
                'POST',
                payload
            );

            // ⚠️ depends on your apiHelper
            if (!response || response.error) {
                throw new Error("API failed");
            }

            // ✅ Success
            setDisabled(false);
            setIsSaving(false);
            setIsDirty(false);
            // sessionStorage.removeItem('platformManageOrder_formData');
            // showAlert('success', 'create', 'Manage Order');
            sessionStorage.setItem(
                "global_alert",
                JSON.stringify({
                    type: "success",
                    action: "update",
                    recordName: "Manage Order"
                })
            );

            // ✅ go back
            history.goBack();

        } catch (error) {
            console.error(error);
            setDisabled(false);
            setIsSaving(false);
            showAlert('error', 'create', 'Manage Order');
        }
    };

    const handleDiscard = () => {
        if (!isDirty || isSaving) {
            sessionStorage.removeItem('platformManageOrder_formData');
            history.goBack();
            return;
        }
        setDiscardModalOpen(true);
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            sessionStorage.removeItem('platformManageOrder_formData');
            setIsDirty(false);
            setFormData({ settlement_price: '', currency: 'SGD', linked_stock_instance: '', notes: '' });
            history.goBack();
        }
        setDiscardModalOpen(false);
    };

    const handleCancelDiscard = () => setDiscardModalOpen(false);

    const handleBlockNavigation = () => {
        if (isSaving || !isDirty) return false;
        setDiscardModalOpen(true);
        return true;
    };

    // ── Pre-populate linkedInstances from existing order data ─────────────────
    useEffect(() => {
        const order = location.state?.order;

        if (order?.stockInstances && order.stockInstances.length > 0) {
            setLinkedInstances(
                order.stockInstances.map((si, index) => ({
                    id: si.platform_order_stockinstance_link_id ?? `${si.stock_instance_id}_order_${index}`,
                    stockInstanceId: si.stock_instance_id,
                    name: si.stockName,
                    variant: si.stockSku,
                    condition: si.condition,
                    grade: si.condition_type,
                    serialNo: si.skuId,
                    branch: si.branch,
                    branchId: si.branch_id ?? si.branchId,
                    costPrice: si.price,
                    currency: 'SGD',
                    qty: si.instanceQty || 1,
                    orderItemId: si.platform_order_item_id,
                    platform_order_stockinstance_link_id: si.platform_order_stockinstance_link_id
                }))
            );
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // ─────────────────────────────────────────────────────────────────────────

    // ── Auto-unlink all StockInstances when order is Cancelled ───────────────
    useEffect(() => {
        if (isCancelled && linkedInstances.length > 0) {
            setLinkedInstances([]);
        }
    }, [isCancelled]);
    // ─────────────────────────────────────────────────────────────────────────

    // ── Handle confirmed linked instances ────────────────────────────────────
    const handleLinkConfirm = (selected) => {
        setLinkedInstances(selected);
        if (!isDirty) setIsDirty(true);
        setLinkModalOpen(false);
    };
    // ─────────────────────────────────────────────────────────────────────────


    const handleBackToPlatform = () => {
        history.goBack();
    };

    return (
        <>
            <div
                className="company-body main-company-body-inner new-look"
                style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
            >
                {showItemAlert && alert && (
                    <AlertNotification
                        type={alert.type}
                        action={alert.action}
                        recordName={alert.recordName}
                        onClose={() => setShowItemAlert(false)}
                    />
                )}

                {/* Breadcrumb */}
                <div className="breadcrumb">
                    <span className="breadcrumb-item">Order - Platform </span>
                    <span
                        className="breadcrumb-item"
                        onClick={handleBackToPlatform}
                        style={{ color: '#6C757D', cursor: 'pointer' }}
                    >
                        {platformLabel}{' '}
                    </span>
                    <span className="breadcrumb-item">Manage Order</span>
                </div>

                {/* Header */}
                <div className='edit-organisation-info'>
                    <div className='edit-organisation-left'>
                        <h1>Manage Order</h1>
                    </div>
                    <div className='discard-save-organisation-right d-flex align-items-center gap-2'>
                        <button
                            className="dark-button"
                            type='button'
                            onClick={handleDiscard}
                            style={{ width: '100px', padding: '8px', height: '34px' }}
                        >
                            Discard
                        </button>
                        <DiscardConfirmationModal
                            isOpen={discardModalOpen}
                            onCancel={handleCancelDiscard}
                            onConfirm={handleDiscardConfirm}
                            entityName="Manage Order"
                        />
                        <button
                            className="dark-border"
                            type='button'
                            onClick={handleSave}
                            disabled={disabled}
                            style={{ width: '100px', padding: '8px', height: '34px' }}
                        >
                            Save
                        </button>
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '24px' }}>
                    <div className="info-section">

                        {/* ── Prices card ── */}
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header">
                                <h4 className="info-title">Prices</h4>
                            </div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label
                                                    htmlFor="settlementPrice"
                                                    className="form-label d-flex align-items-center gap-2"
                                                >
                                                    Settlement Price
                                                    {/* <span className='error-color'>*</span> */}
                                                    <Tooltip
                                                        title="Settlement Price"
                                                        body="The Price we actually received from selling this Order (after deducting platform fees, discount and any other costs). Manually inputted on ERP."
                                                    >
                                                        <Info size={16} className="info-icon" />
                                                    </Tooltip>
                                                </label>

                                                <div
                                                    className={`form-control d-flex align-items-center p-0 ${errors.settlement_price ? 'err-color' : ''
                                                        }`}
                                                    style={{
                                                        overflow: 'hidden',
                                                        height: '39px',
                                                        display: 'flex',
                                                        border: `1px solid ${errors.settlement_price ? '' : '#6c757d'}`,
                                                        outline: 'none',
                                                        boxShadow: 'none'
                                                    }}
                                                >
                                                    {/* Currency */}
                                                    <div
                                                        style={{
                                                            width: '60px',
                                                            background: '#FFF',
                                                            borderRight: '1px solid #212529',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '14px',
                                                            height: '100%',
                                                            padding: '10px',
                                                            fontFamily: 'Montserrat'
                                                        }}
                                                    >
                                                        {formData.currency}
                                                    </div>

                                                    {/* Input + Icon wrapper */}
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flex: 1,
                                                            paddingRight: '8px',
                                                        }}
                                                    >
                                                        <input
                                                            type="text"
                                                            id="settlementPrice"
                                                            name="settlement_price"
                                                            value={settlementFocused ? formData.settlement_price : formatSettlementDisplay(formData.settlement_price)}
                                                            onChange={handleChange}
                                                            onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                                                            onFocus={() => setSettlementFocused(true)}
                                                            onBlur={() => {
                                                                setSettlementFocused(false);
                                                                const num = parseFloat(String(formData.settlement_price).replace(/,/g, ''));
                                                                if (!isNaN(num)) setFormData(prev => ({ ...prev, settlement_price: num.toFixed(2) }));
                                                            }}
                                                            placeholder="0.00"
                                                            style={{
                                                                flex: 1,
                                                                border: 'none',
                                                                outline: 'none',
                                                                background: 'transparent',
                                                                padding: '10px',
                                                                fontSize: '14px',
                                                                height: '100%',
                                                            }}
                                                        />

                                                        {/* SVG inside input area */}
                                                        {formData.settlement_price && (
                                                            <span
                                                                onClick={() =>
                                                                    setFormData({ ...formData, settlement_price: '' })
                                                                }
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="12"
                                                                    height="12"
                                                                    viewBox="0 0 12 12"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        d="M9 3L3 9M3 3L9 9"
                                                                        stroke="#6C757D"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* {errors.settlement_price && (
                                                    <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
                                                        {errors.settlement_price}
                                                    </p>
                                                )} */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Linked StockInstances card ── */}
                        {!isCancelled && (<div className="info-card new-info-overflow-auto">
                            <div className="info-header">
                                <h4 className="info-title">Linked StockInstances</h4>
                            </div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>
                                    <div className='row gappingcls-added'>
                                        <div className='col-12'>
                                            <div className="info-inner" style={{
                                                background: '#FFF',
                                                padding: '20px',
                                                borderRadius: '12px',
                                                border: '1px solid #E9ECEF'
                                            }}>

                                                {linkedInstances.length > 0 && (
                                                    <div style={{
                                                        margin: 20,
                                                        overflow: 'hidden'
                                                    }}>

                                                        <table style={{
                                                            width: '100%',
                                                            borderCollapse: 'collapse',
                                                            fontFamily: 'Montserrat',
                                                            fontSize: 13
                                                        }}>

                                                            {/* HEADER */}
                                                            <thead style={{ background: '#F1F3F5' }}>
                                                                <tr>
                                                                    <th style={{ padding: 12, width: 40 }}></th>
                                                                    <th style={{ padding: 12, textAlign: 'left' }}>Stock</th>
                                                                    <th style={{ padding: 12, textAlign: 'left' }}>StockInstance</th>
                                                                    <th style={{ padding: 12, textAlign: 'left' }}>Qty</th>
                                                                    <th style={{ width: 40 }}></th>
                                                                </tr>
                                                            </thead>

                                                            {/* BODY */}
                                                            <tbody>
                                                                {linkedInstances.map((item, index) => (
                                                                    <tr
                                                                        key={item.id || index}
                                                                        draggable
                                                                        onDragStart={() => { dragIndexRef.current = index; }}
                                                                        onDragOver={e => e.preventDefault()}
                                                                        onDrop={() => {
                                                                            const from = dragIndexRef.current;
                                                                            if (from === null || from === index) return;
                                                                            setLinkedInstances(prev => {
                                                                                const next = [...prev];
                                                                                const [moved] = next.splice(from, 1);
                                                                                next.splice(index, 0, moved);
                                                                                return next;
                                                                            });
                                                                            dragIndexRef.current = null;
                                                                        }}
                                                                        style={{ borderTop: '1px solid #E9ECEF', background: index % 2 === 0 ? '#fff' : '#F8F9FA' }}
                                                                    >

                                                                        {/* DELETE ICON */}
                                                                        <td style={{ padding: 12 }}>
                                                                            <Trash2
                                                                                size={16}
                                                                                style={{ cursor: 'pointer', color: '#6C757D' }}
                                                                                onClick={() => setLinkedInstances(prev => prev.filter((_, i) => i !== index))}
                                                                            />
                                                                        </td>

                                                                        {/* STOCK */}
                                                                        <td style={{ padding: 12 }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                                <img
                                                                                    src={item?.image || noImage}
                                                                                    alt="product"
                                                                                    style={{ width: 32, height: 32 }}
                                                                                />
                                                                                <div>
                                                                                    <div style={{ fontWeight: 400, color: '#212529', fontSize: 12 }}>{item?.name}</div>
                                                                                    <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2 }}>{item?.variant}</div>
                                                                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                                                        <span style={{ background: '#FFEB00', borderRadius: 99, padding: '2px 5px', fontSize: 8, fontWeight: 600, color: '#212529', border: '1px solid #FFEB00' }}>
                                                                                            {item?.condition}
                                                                                        </span>
                                                                                        <span style={{ background: '#FFEB00', borderRadius: 99, padding: '2px 5px', fontSize: 8, fontWeight: 600, color: '#212529', border: '1px solid #FFEB00' }}>
                                                                                            {item?.grade}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </td>

                                                                        {/* STOCK INSTANCE */}
                                                                        <td style={{ padding: 12 }}>
                                                                            <div>{item?.serialNo}</div>
                                                                            <div style={{ fontSize: 12, color: '#6C757D' }}>
                                                                                {item?.branch} | {item?.currency} {item?.costPrice != null ? Number(item.costPrice).toFixed(2) : ''}
                                                                            </div>
                                                                        </td>

                                                                        {/* QTY */}
                                                                        <td style={{ padding: 12 }}>
                                                                            {item?.qty}
                                                                        </td>

                                                                        {/* DRAG HANDLE */}
                                                                        <td style={{ padding: 12, cursor: 'grab' }}>
                                                                            <Menu size={16} color="#6C757D" />
                                                                        </td>

                                                                    </tr>
                                                                ))}
                                                            </tbody>

                                                        </table>
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="dark-button"
                                                    onClick={() => setLinkModalOpen(true)}   // ← UPDATED
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: '10px',
                                                        height: '34px',
                                                        gap: '10px',
                                                        backgroundColor: '#FFF',
                                                        color: '#212529',
                                                        border: '1px solid #212529',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        fontFamily: 'Montserrat',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                                    }}
                                                >
                                                    Link StockInstance
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>)}

                        {!discardModalOpen && (
                            <UnsavedChangesPrompt
                                isDirty={isDirty && !isSaving}
                                when={isDirty && !isSaving}
                                entityName={'Manage Order'}
                                navigate={handleBlockNavigation}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Link StockInstances Modal ── */}
            <LinkStockInstancesModal
                isOpen={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                onConfirm={handleLinkConfirm}
                preSelected={linkedInstances}
            />
        </>
    );
}
